/**
 * Tela /import-mappings/:id e /import-mappings/new (TH-53217 / M2.E7.S3.1).
 *
 * Editor do `config` JSON do ImportMapping. Cada linha corresponde a um
 * campo destino (sku/name/price/promo_price/...) → coluna da planilha.
 *
 * Schema do backend (apps/api/.../schemas/import_mapping.py):
 *   {
 *     "sku":   { "column": "Codigo", "required": true },
 *     "name":  { "column": "Produto", "required": true },
 *     "price": { "column": "Preço", "required": true, "format": "money_br" },
 *     "promo_price": { "column": "Promo", "required": false, "format": "money_br" }
 *   }
 *
 * Regras (validadas server-side, replicadas client-side):
 *   - sku, name e price são obrigatórios (required=true forçado).
 *   - format opcional, mas se presente precisa ser "money_br" ou "date_br".
 *   - column não pode ser vazio.
 *   - destination_field não pode duplicar nem ser vazio.
 *
 * Reordenação: setas ↑ ↓ (drag&drop fora de scope; lib não está no
 * package.json e o config é dict — ordem é cosmética, mas ajuda no UX).
 */

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowDown, ArrowUp, Trash2 } from "lucide-react";

import { AppLayout } from "@/components/AppLayout";
import { RequireAuth } from "@/components/RequireAuth";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Select } from "@/components/ui/Select";
import { useAuth } from "@/hooks/useAuth";

const ROLES_AUTHORIZED = new Set([
  "super_admin",
  "tenant_admin",
  "store_manager",
  "operator",
]);

/**
 * Catálogo de formatos aceitos pelo backend (TH-55120).
 *
 * Cada formato tem categoria (pra agrupamento no dropdown), label em
 * pt-BR e exemplo curto. Espelha `_AVAILABLE_FORMATS` em
 * `apps/api/priceshow_api/routers/import_mappings.py`. Backend é
 * source-of-truth; opcionalmente o admin pode fetchar `/import-mappings/formats`
 * em vez de hardcodar — feito hardcoded por simplicidade (lista é curta
 * e raramente muda).
 */
const FORMAT_CATALOG = [
  { key: "money_br", category: "Numérico", label: "Moeda BR", example: "R$ 12,90 — 1.299,90" },
  { key: "money_us", category: "Numérico", label: "Moeda US", example: "1,234.56 — $12.90" },
  { key: "money_cents", category: "Numérico", label: "Centavos (inteiro)", example: "1290 (= R$ 12,90)" },
  { key: "percentage", category: "Numérico", label: "Percentual", example: "25% — 25,5% — 0,25" },
  { key: "integer_br", category: "Numérico", label: "Inteiro com milhar BR", example: "1.234 = 1234" },
  { key: "date_br", category: "Data", label: "Data BR / ISO", example: "25/04/2026 — 2026-04-25" },
  { key: "ean", category: "Identificador", label: "EAN-8/13 com check digit", example: "7891000100103" },
  { key: "ean_loose", category: "Identificador", label: "EAN sem validar check digit", example: "1234567890123" },
  { key: "gtin14", category: "Identificador", label: "GTIN-14 (caixa/fardo)", example: "17891000100100" },
  { key: "internal_code", category: "Identificador", label: "Código interno (texto opaco)", example: "ABC-001-X" },
  { key: "boolean_br", category: "Booleano", label: "Booleano BR", example: "Sim/Não — S/N — 1/0" },
  { key: "weight_unit", category: "Peso/Volume", label: "Peso/volume com unidade", example: "500g — 1,5kg — 2L" },
  { key: "text", category: "Texto", label: "Texto livre (até 200 chars)", example: "Banana Prata Orgânica" },
] as const;

const ALLOWED_FORMATS = FORMAT_CATALOG.map((f) => f.key) as readonly string[];
type AllowedFormat = (typeof FORMAT_CATALOG)[number]["key"];

/** Categorias na ordem que aparecem no dropdown (agrupamento via optgroup). */
const FORMAT_CATEGORIES = [
  "Numérico",
  "Data",
  "Identificador",
  "Booleano",
  "Peso/Volume",
  "Texto",
] as const;

const REQUIRED_FIELDS = new Set(["sku", "name", "price"]);

/**
 * Catálogo de campos-alvo do mapeamento.
 *
 * Espelha os 7 campos canônicos reconhecidos pelo backend em
 * `apps/api/priceshow_api/services/imports/mapping.py` (`_FIELD_SYNONYMS`).
 * `name` na UI vira "Descrição"; o campo `ean` é fundamental para
 * identificar produto (TH-55121). Manter sincronizado com o backend.
 *
 * `format` é o default sugerido ao escolher o campo (admin pode trocar).
 */
const KNOWN_TARGET_FIELDS: ReadonlyArray<{
  key: string;
  label: string;
  hint: string;
  defaultFormat: AllowedFormat | "";
}> = [
  { key: "sku", label: "SKU / Código", hint: "código interno do produto", defaultFormat: "" },
  { key: "ean", label: "EAN / Código de barras", hint: "GTIN/EAN-13", defaultFormat: "" },
  { key: "name", label: "Descrição / Nome", hint: "texto exibido no slide", defaultFormat: "" },
  { key: "price", label: "Preço regular", hint: "valor cheio", defaultFormat: "money_br" },
  { key: "promo_price", label: "Preço promocional", hint: "valor da oferta", defaultFormat: "money_br" },
  { key: "valid_until", label: "Validade da oferta (até)", hint: "data fim da promo", defaultFormat: "date_br" },
  { key: "unit", label: "Unidade de medida", hint: "kg, un, L", defaultFormat: "" },
] as const;

const KNOWN_KEYS = new Set(KNOWN_TARGET_FIELDS.map((f) => f.key));

const CUSTOM_FIELD_SENTINEL = "__custom__";

interface ConfigSpec {
  column: string;
  required?: boolean;
  format?: string;
}

interface ImportMappingRead {
  id: string;
  name: string;
  config: Record<string, ConfigSpec>;
  created_at: string;
}

/** Linha do array editável; `key` é o nome do campo destino. */
interface FieldRow {
  /** id estável usado como react key (independente da `key`). */
  uid: string;
  key: string;
  column: string;
  required: boolean;
  format: AllowedFormat | "";
}

const DEFAULT_FIELDS: ReadonlyArray<Omit<FieldRow, "uid">> = [
  { key: "sku", column: "", required: true, format: "" },
  { key: "name", column: "", required: true, format: "" },
  { key: "price", column: "", required: true, format: "money_br" },
];

let uidCounter = 0;
function nextUid(): string {
  uidCounter += 1;
  return `f-${uidCounter}`;
}

function withUid(row: Omit<FieldRow, "uid">): FieldRow {
  return { ...row, uid: nextUid() };
}

function rowsFromConfig(config: Record<string, ConfigSpec>): FieldRow[] {
  return Object.entries(config).map(([key, spec]) =>
    withUid({
      key,
      column: spec.column ?? "",
      required: Boolean(spec.required),
      format: ALLOWED_FORMATS.includes(spec.format as AllowedFormat)
        ? (spec.format as AllowedFormat)
        : "",
    }),
  );
}

function configFromRows(
  rows: ReadonlyArray<FieldRow>,
): Record<string, ConfigSpec> {
  const out: Record<string, ConfigSpec> = {};
  for (const r of rows) {
    const spec: ConfigSpec = { column: r.column };
    if (r.required) spec.required = true;
    if (r.format) spec.format = r.format;
    out[r.key] = spec;
  }
  return out;
}

interface ValidationResult {
  ok: boolean;
  globalError: string | null;
  /** Erros indexados por uid da linha. */
  rowErrors: Record<string, string>;
}

function validate(name: string, rows: ReadonlyArray<FieldRow>): ValidationResult {
  const rowErrors: Record<string, string> = {};
  let globalError: string | null = null;

  if (!name.trim()) {
    globalError = "Nome obrigatório";
  } else if (name.trim().length > 120) {
    globalError = "Nome muito longo (máx 120 caracteres)";
  }

  const seen = new Set<string>();
  for (const r of rows) {
    if (!r.key.trim()) {
      rowErrors[r.uid] = "Campo destino obrigatório";
      continue;
    }
    if (seen.has(r.key)) {
      rowErrors[r.uid] = `Campo duplicado: ${r.key}`;
      continue;
    }
    seen.add(r.key);
    if (!r.column.trim()) {
      rowErrors[r.uid] = "Coluna da planilha obrigatória";
      continue;
    }
    if (REQUIRED_FIELDS.has(r.key) && !r.required) {
      rowErrors[r.uid] = `${r.key} precisa estar marcado como obrigatório`;
      continue;
    }
  }

  for (const required of REQUIRED_FIELDS) {
    if (!rows.some((r) => r.key === required)) {
      globalError ??= `Campos obrigatórios faltando: ${[...REQUIRED_FIELDS]
        .filter((f) => !rows.some((r) => r.key === f))
        .join(", ")}`;
      break;
    }
  }

  const ok = !globalError && Object.keys(rowErrors).length === 0;
  return { ok, globalError: globalError ?? null, rowErrors };
}

async function fetchMapping(id: string): Promise<ImportMappingRead> {
  const res = await fetch(`/api/import-mappings/${id}`, {
    credentials: "include",
  });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  return (await res.json()) as ImportMappingRead;
}

interface SavePayload {
  name: string;
  config: Record<string, ConfigSpec>;
}

async function createMapping(payload: SavePayload): Promise<ImportMappingRead> {
  const res = await fetch("/api/import-mappings", {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: unknown };
    throw new Error(formatDetail(body.detail) ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as ImportMappingRead;
}

async function updateMapping(
  id: string,
  payload: SavePayload,
): Promise<ImportMappingRead> {
  const res = await fetch(`/api/import-mappings/${id}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: unknown };
    throw new Error(formatDetail(body.detail) ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as ImportMappingRead;
}

async function deleteMapping(id: string): Promise<void> {
  const res = await fetch(`/api/import-mappings/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok && res.status !== 204) {
    const body = (await res.json().catch(() => ({}))) as { detail?: unknown };
    throw new Error(formatDetail(body.detail) ?? `HTTP ${res.status}`);
  }
}

function formatDetail(detail: unknown): string | null {
  if (!detail) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    // FastAPI 422 vira lista de {loc, msg, type}.
    return detail
      .map((d) => {
        if (d && typeof d === "object" && "msg" in d) {
          return String((d as { msg: unknown }).msg);
        }
        return JSON.stringify(d);
      })
      .join("; ");
  }
  return JSON.stringify(detail);
}

/**
 * Picker do campo destino (TH-55121).
 *
 * Dropdown com os 7 campos canônicos do backend + opção "Outro…" que
 * libera input livre para nomes customizados (retrocompatibilidade com
 * configs antigas e fuga de emergência caso o backend evolua antes do
 * frontend).
 *
 * Quando o usuário seleciona um campo canônico, sugere o `format`
 * default (ex.: money_br para price) — mas só se o admin ainda não
 * tiver definido manualmente.
 */
interface FieldKeyPickerProps {
  value: string;
  invalid?: boolean;
  takenKeys: Set<string>;
  onChange: (key: string, suggestedFormat?: AllowedFormat | "") => void;
}

function FieldKeyPicker({
  value,
  invalid,
  takenKeys,
  onChange,
}: FieldKeyPickerProps) {
  const isKnown = KNOWN_KEYS.has(value);
  const startedCustom = value !== "" && !isKnown;
  const [showCustomInput, setShowCustomInput] = useState(startedCustom);

  // Se o valor (vindo de fora) ficar conhecido novamente, ocultamos o
  // input livre. Se ficar custom, mostramos.
  useEffect(() => {
    if (value === "" || isKnown) {
      setShowCustomInput(false);
    } else {
      setShowCustomInput(true);
    }
  }, [value, isKnown]);

  // Valor do <select>: campo canônico, ou sentinel "Outro…" quando
  // estiver em modo custom ou vazio com input aberto.
  const selectValue = isKnown ? value : showCustomInput ? CUSTOM_FIELD_SENTINEL : "";

  function handleSelect(e: React.ChangeEvent<HTMLSelectElement>) {
    const v = e.target.value;
    if (v === CUSTOM_FIELD_SENTINEL) {
      setShowCustomInput(true);
      // Não muda a `key` ainda — espera o usuário digitar.
      // Mas se o valor atual era canônico, limpa para evitar conflito
      // com a opção desabilitada do dropdown.
      if (isKnown) onChange("");
      return;
    }
    setShowCustomInput(false);
    const def = KNOWN_TARGET_FIELDS.find((f) => f.key === v);
    onChange(v, def?.defaultFormat ?? "");
  }

  return (
    <div className="flex flex-col gap-1">
      <Select
        value={selectValue}
        onChange={handleSelect}
        aria-label="Campo destino"
        aria-invalid={invalid || undefined}
        className="h-8 text-xs"
      >
        <option value="" disabled>
          — selecione —
        </option>
        {KNOWN_TARGET_FIELDS.map((f) => {
          const taken = takenKeys.has(f.key);
          return (
            <option key={f.key} value={f.key} disabled={taken}>
              {f.label}
              {taken ? " (já usado)" : ""}
            </option>
          );
        })}
        <option value={CUSTOM_FIELD_SENTINEL}>Outro… (customizado)</option>
      </Select>
      {showCustomInput ? (
        <Input
          value={value}
          onChange={(e) =>
            onChange(e.target.value.trim().toLowerCase(), undefined)
          }
          placeholder="nome do campo (ex.: brand)"
          className="h-8 font-mono text-xs"
          aria-label="Nome do campo customizado"
        />
      ) : null}
    </div>
  );
}

export function ImportMappingEditPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const params = useParams({ strict: false }) as { mappingId?: string };
  const rawId = params.mappingId;
  const isNew = !rawId || rawId === "new";
  const mappingId = isNew ? null : rawId!;

  const canManage = user ? ROLES_AUTHORIZED.has(user.role) : false;

  const query = useQuery({
    queryKey: ["import-mapping", mappingId],
    queryFn: () => fetchMapping(mappingId!),
    enabled: !!mappingId && canManage,
  });

  const [name, setName] = useState("");
  const [rows, setRows] = useState<FieldRow[]>(() =>
    DEFAULT_FIELDS.map((f) => withUid(f)),
  );
  const [serverError, setServerError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  // Hidrata estado a partir da query quando entra no modo edit. Roda
  // só uma vez por id (controle via `hydrated`) para não sobrescrever
  // edições do usuário caso a query revalide.
  useEffect(() => {
    if (!isNew && query.data && !hydrated) {
      setName(query.data.name);
      setRows(rowsFromConfig(query.data.config));
      setHydrated(true);
    }
  }, [isNew, query.data, hydrated]);

  const validation = useMemo(() => validate(name, rows), [name, rows]);

  const save = useMutation({
    mutationFn: async () => {
      const payload: SavePayload = {
        name: name.trim(),
        config: configFromRows(rows),
      };
      return mappingId
        ? updateMapping(mappingId, payload)
        : createMapping(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["import-mappings"] });
      if (mappingId) {
        queryClient.invalidateQueries({
          queryKey: ["import-mapping", mappingId],
        });
      }
      navigate({ to: "/import-mappings" });
    },
    onError: (err: Error) => setServerError(err.message),
  });

  const remove = useMutation({
    mutationFn: () => deleteMapping(mappingId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["import-mappings"] });
      navigate({ to: "/import-mappings" });
    },
    onError: (err: Error) => setServerError(err.message),
  });

  if (user && !canManage) {
    return (
      <RequireAuth>
        <AppLayout
          breadcrumb="OPERAÇÃO / IMPORTAÇÕES / MAPEAMENTOS"
          title="Acesso negado"
        >
          <div className="mx-auto max-w-3xl">
            <p className="text-sm text-[var(--color-muted-foreground)]">
              Esta tela está disponível para super_admin, tenant_admin,
              store_manager ou operator.
            </p>
          </div>
        </AppLayout>
      </RequireAuth>
    );
  }

  const isLoading = !isNew && query.isLoading;
  const loadError = !isNew && query.error;

  function moveRow(idx: number, delta: number) {
    setRows((prev) => {
      const next = [...prev];
      const target = idx + delta;
      if (target < 0 || target >= next.length) return prev;
      const [moved] = next.splice(idx, 1);
      next.splice(target, 0, moved!);
      return next;
    });
  }

  function updateRow(uid: string, patch: Partial<FieldRow>) {
    setRows((prev) =>
      prev.map((r) => {
        if (r.uid !== uid) return r;
        const next = { ...r, ...patch };
        // Auto-marca required=true em campos do sistema.
        if (REQUIRED_FIELDS.has(next.key)) next.required = true;
        return next;
      }),
    );
  }

  function removeRow(uid: string) {
    setRows((prev) => prev.filter((r) => r.uid !== uid));
  }

  function addRow() {
    setRows((prev) => [
      ...prev,
      withUid({ key: "", column: "", required: false, format: "" }),
    ]);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setServerError(null);
    if (!validation.ok) return;
    save.mutate();
  }

  const title = isNew ? "Novo mapeamento" : name || "Mapeamento";

  return (
    <RequireAuth>
      <AppLayout
        breadcrumb="OPERAÇÃO / IMPORTAÇÕES / MAPEAMENTOS"
        title={title}
      >
        <div className="mx-auto max-w-4xl">
          {loadError ? (
            <div className="mb-4 rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 px-3 py-2 text-xs text-[var(--color-destructive)]">
              erro ao carregar mapeamento
            </div>
          ) : null}

          {isLoading ? (
            <p className="text-sm text-[var(--color-muted-foreground)]">
              carregando…
            </p>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="flex flex-col gap-6"
              noValidate
            >
              <Card className="p-4">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="mapping-name">Nome do mapeamento</Label>
                  <Input
                    id="mapping-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Ex.: Layout ERP padrão"
                    maxLength={120}
                    autoFocus={isNew}
                  />
                  <p className="text-xs text-[var(--color-muted-foreground)]">
                    Identifica o layout no wizard de importação. Use um nome
                    curto e descritivo (ex.: "Layout SAP filiais SP").
                  </p>
                </div>
              </Card>

              <Card className="p-0">
                <div className="flex items-center justify-between border-b border-[var(--color-border)] px-[18px] py-[14px]">
                  <div>
                    <div className="text-sm font-semibold">
                      Campos do mapeamento
                    </div>
                    <div className="mt-0.5 text-[11px] text-[var(--color-muted-foreground)]">
                      SKU, Descrição e Preço regular são obrigatórios. Demais
                      campos (EAN, preço promocional, validade, unidade…) são
                      opcionais. Use "Outro…" para nomes customizados.
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addRow}
                  >
                    + Adicionar campo
                  </Button>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-xs">
                    <thead className="bg-[var(--color-table-row-tint)]">
                      <tr>
                        <th className="w-[60px] px-[18px] py-2.5 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--color-muted-foreground)]" />
                        <th className="px-[18px] py-2.5 text-left font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--color-muted-foreground)]">
                          Campo destino
                        </th>
                        <th className="px-[18px] py-2.5 text-left font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--color-muted-foreground)]">
                          Coluna da planilha
                        </th>
                        <th className="px-[18px] py-2.5 text-left font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--color-muted-foreground)]">
                          Formato
                        </th>
                        <th className="px-[18px] py-2.5 text-center font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--color-muted-foreground)]">
                          Obrigatório
                        </th>
                        <th className="w-[60px] px-[18px] py-2.5 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--color-muted-foreground)]" />
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length === 0 ? (
                        <tr>
                          <td
                            colSpan={6}
                            className="px-[18px] py-8 text-center text-[var(--color-muted-foreground)]"
                          >
                            nenhum campo — clique em "+ Adicionar campo"
                          </td>
                        </tr>
                      ) : (
                        rows.map((row, idx) => {
                          const error = validation.rowErrors[row.uid];
                          const isRequiredField = REQUIRED_FIELDS.has(row.key);
                          // Keys já usadas em outras linhas — usado pelo
                          // picker para desabilitar opções duplicadas.
                          const takenKeys = new Set(
                            rows
                              .filter((r) => r.uid !== row.uid && r.key)
                              .map((r) => r.key),
                          );
                          return (
                            <tr
                              key={row.uid}
                              className="border-t border-[var(--color-border)] align-top"
                            >
                              <td className="px-[12px] py-[10px]">
                                <div className="flex flex-col gap-0.5">
                                  <Button
                                    type="button"
                                    size="icon"
                                    variant="ghost"
                                    aria-label="Mover para cima"
                                    disabled={idx === 0}
                                    className="h-6 w-6"
                                    onClick={() => moveRow(idx, -1)}
                                  >
                                    <ArrowUp className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button
                                    type="button"
                                    size="icon"
                                    variant="ghost"
                                    aria-label="Mover para baixo"
                                    disabled={idx === rows.length - 1}
                                    className="h-6 w-6"
                                    onClick={() => moveRow(idx, 1)}
                                  >
                                    <ArrowDown className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              </td>
                              <td className="px-[12px] py-[10px]">
                                <FieldKeyPicker
                                  value={row.key}
                                  invalid={Boolean(error)}
                                  takenKeys={takenKeys}
                                  onChange={(newKey, newFormat) => {
                                    const patch: Partial<FieldRow> = {
                                      key: newKey,
                                    };
                                    // Aplica format default só quando o
                                    // usuário escolheu um campo canônico
                                    // e ainda não definiu format manual.
                                    if (newFormat !== undefined && !row.format) {
                                      patch.format = newFormat;
                                    }
                                    updateRow(row.uid, patch);
                                  }}
                                />
                                {error ? (
                                  <p
                                    role="alert"
                                    className="mt-1 text-[11px] text-[var(--color-destructive)]"
                                  >
                                    {error}
                                  </p>
                                ) : null}
                              </td>
                              <td className="px-[12px] py-[10px]">
                                <Input
                                  value={row.column}
                                  onChange={(e) =>
                                    updateRow(row.uid, {
                                      column: e.target.value,
                                    })
                                  }
                                  placeholder="Codigo, Produto, Preço…"
                                  className="h-8 text-xs"
                                />
                              </td>
                              <td className="px-[12px] py-[10px]">
                                <Select
                                  value={row.format}
                                  onChange={(e) =>
                                    updateRow(row.uid, {
                                      format: e.target.value as
                                        | AllowedFormat
                                        | "",
                                    })
                                  }
                                  className="h-8 text-xs"
                                >
                                  <option value="">— nenhum —</option>
                                  {FORMAT_CATEGORIES.map((cat) => {
                                    const opts = FORMAT_CATALOG.filter(
                                      (f) => f.category === cat,
                                    );
                                    if (opts.length === 0) return null;
                                    return (
                                      <optgroup key={cat} label={cat}>
                                        {opts.map((f) => (
                                          <option
                                            key={f.key}
                                            value={f.key}
                                            title={f.example}
                                          >
                                            {f.label}
                                          </option>
                                        ))}
                                      </optgroup>
                                    );
                                  })}
                                </Select>
                              </td>
                              <td className="px-[12px] py-[10px] text-center">
                                <input
                                  type="checkbox"
                                  checked={row.required}
                                  disabled={isRequiredField}
                                  onChange={(e) =>
                                    updateRow(row.uid, {
                                      required: e.target.checked,
                                    })
                                  }
                                  className="h-4 w-4 cursor-pointer accent-[var(--color-primary)] disabled:cursor-not-allowed"
                                  aria-label={`required ${row.key || idx}`}
                                />
                              </td>
                              <td className="px-[12px] py-[10px]">
                                <Button
                                  type="button"
                                  size="icon"
                                  variant="ghost"
                                  aria-label="Remover campo"
                                  className="text-[var(--color-destructive)]"
                                  disabled={isRequiredField}
                                  title={
                                    isRequiredField
                                      ? "Campo obrigatório do sistema"
                                      : undefined
                                  }
                                  onClick={() => removeRow(row.uid)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>

              {validation.globalError ? (
                <div
                  role="alert"
                  className="rounded-md border border-[var(--color-destructive)]/40 bg-[var(--color-destructive)]/10 px-3 py-2 text-xs text-[var(--color-destructive)]"
                >
                  {validation.globalError}
                </div>
              ) : null}

              {serverError ? (
                <div
                  role="alert"
                  className="rounded-md border border-[var(--color-destructive)]/40 bg-[var(--color-destructive)]/10 px-3 py-2 text-xs text-[var(--color-destructive)]"
                >
                  {serverError}
                </div>
              ) : null}

              <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate({ to: "/import-mappings" })}
                  disabled={save.isPending || remove.isPending}
                >
                  ← Voltar
                </Button>

                <div className="flex gap-2">
                  {!isNew ? (
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={() => setConfirmDelete(true)}
                      disabled={save.isPending || remove.isPending}
                    >
                      Deletar
                    </Button>
                  ) : null}
                  <Button
                    type="submit"
                    disabled={
                      !validation.ok ||
                      save.isPending ||
                      remove.isPending
                    }
                  >
                    {save.isPending
                      ? "Salvando…"
                      : isNew
                        ? "Criar mapeamento"
                        : "Salvar alterações"}
                  </Button>
                </div>
              </div>
            </form>
          )}
        </div>

        {confirmDelete && mappingId ? (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
            role="dialog"
            aria-modal="true"
            onClick={() => setConfirmDelete(false)}
          >
            <div
              className="w-full max-w-md rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-6 shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-base font-semibold">Remover mapeamento</h2>
              <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
                Confirma a exclusão de{" "}
                <strong className="text-[var(--color-foreground)]">{name}</strong>?
                Backend bloqueia se já houver batch de importação usando este
                mapping.
              </p>
              {serverError ? (
                <p
                  role="alert"
                  className="mt-3 rounded-md border border-[var(--color-destructive)]/40 bg-[var(--color-destructive)]/10 px-3 py-2 text-xs text-[var(--color-destructive)]"
                >
                  {serverError}
                </p>
              ) : null}
              <div className="mt-5 flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setConfirmDelete(false)}
                  disabled={remove.isPending}
                >
                  Cancelar
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => remove.mutate()}
                  disabled={remove.isPending}
                >
                  {remove.isPending ? "Removendo…" : "Remover"}
                </Button>
              </div>
            </div>
          </div>
        ) : null}
      </AppLayout>
    </RequireAuth>
  );
}
